import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { useValueAssessmentTelemetry } from '@/hooks/useValueAssessmentTelemetry';
import { useValueAssessmentDraft, type ChatDraftData } from '@/hooks/useValueAssessmentDraft';
import { saveCareerContextFromExtraction, type ExtractedCareerContext } from '@/lib/save-career-context';
import { emitEngagementEvent } from '@/lib/engagement-events';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TabStatsLine } from './TabStatsLine';
import { Heart, Send, Loader2, Sparkles, RefreshCw, Trash2, CheckCircle2, ArrowLeft, Unlock } from 'lucide-react';
import { DiscoveryMethodSelector, DiscoveryMethod } from '@/components/values/DiscoveryMethodSelector';
import { ValuesCardSort } from '@/components/values/ValuesCardSort';
import { ReflectionPrompts } from '@/components/values/ReflectionPrompts';
import { QuickAssessment } from '@/components/values/QuickAssessment';
import { ResumeDraftPrompt } from '@/components/values/ResumeDraftPrompt';
import { ValuesCelebration } from '@/components/values/ValuesCelebration';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface CoreValue {
  id: string;
  value_name: string;
  description: string | null;
  importance_rank: number | null;
}

interface ExtractedValue {
  name: string;
  description: string;
  importance: number;
}

interface CoreValuesTabProps {
  onValuesChanged?: () => void;
  onNavigateToGoals?: () => void;
}

export const CoreValuesTab = ({ onValuesChanged, onNavigateToGoals }: CoreValuesTabProps = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { profile, invalidate: invalidateProfile } = useProfile();
  const { toast } = useToast();
  const {
    startAssessment,
    restoreSession,
    trackAIConversationTurn,
    completeAssessment: completeTelemetry,
    abandonAssessment,
    markCompleted
  } = useValueAssessmentTelemetry();
  const { draft, isLoading: isDraftLoading, saveDraft, saveDraftImmediate, deleteDraft } = useValueAssessmentDraft();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [coreValues, setCoreValues] = useState<CoreValue[]>([]);
  const [discoveryMethod, setDiscoveryMethod] = useState<DiscoveryMethod | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [draftData, setDraftData] = useState<Record<string, unknown> | null>(null);
  const [draftAssessmentId, setDraftAssessmentId] = useState<string | null>(null);
  const [pendingMethod, setPendingMethod] = useState<DiscoveryMethod | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCoreValues();
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!isLoading) inputRef.current?.focus();
  }, [messages.length, isLoading]);

  const fetchCoreValues = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('core_values')
      .select('*')
      .eq('user_id', user.id)
      .order('importance_rank', { ascending: true });
    
    if (data && !error) {
      setCoreValues(data);
    }
  };

  const deleteExistingValues = async () => {
    if (!user) return;
    const { error } = await supabase
      .from('core_values')
      .delete()
      .eq('user_id', user.id);
    if (error) {
      console.error('Error deleting existing values:', error);
    }
  };

  const notifyValuesChanged = async () => {
    await invalidateProfile();
    queryClient.invalidateQueries({ queryKey: ['coach-insights-values'] });
    queryClient.invalidateQueries({ queryKey: ['core-values-badge'] });
    onValuesChanged?.();
  };

  const startDiscovery = () => {
    setIsDiscovering(true);
    setMessages([]);
    sendMessage('', true);
  };

  const sendMessage = async (userMessage: string, isInitial = false) => {
    if (!isInitial && !userMessage.trim()) return;
    
    const newMessages: Message[] = isInitial 
      ? [] 
      : [...messages, { role: 'user' as const, content: userMessage }];
    
    if (!isInitial) {
      setMessages(newMessages);
      setInput('');
    }
    
    setIsLoading(true);
    let assistantContent = '';

    try {
      // Get current session for authenticated request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Build profile context for career context phase
      const profileContext = {
        job_role: profile?.job_role || null,
        target_job_role: profile?.target_job_role || null,
        industry: profile?.industry || null,
      };

      // Emit career_context_shown for fresh conversations with missing fields
      if (isInitial && (!profileContext.job_role || !profileContext.target_job_role || !profileContext.industry)) {
        emitEngagementEvent('career_context_shown', { missing_fields: [
          ...(!profileContext.job_role ? ['job_role'] : []),
          ...(!profileContext.target_job_role ? ['target_job_role'] : []),
          ...(!profileContext.industry ? ['industry'] : []),
        ]});
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discover-values`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ messages: newMessages, profileContext }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429 && errorData.retry_after_seconds) {
          const seconds = errorData.retry_after_seconds;
          const timeMsg = seconds >= 60 ? `${Math.ceil(seconds / 60)} minute(s)` : `${seconds} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        if (response.status === 402) {
          throw new Error('AI_UNAVAILABLE');
        }
        throw new Error(errorData.error || 'Failed to get response');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => 
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
            }
          } catch {
            // Skip invalid JSON
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'AI_UNAVAILABLE') {
        toast({
          title: 'AI temporarily unavailable',
          description: 'Try the Quick Assessment instead — no AI needed.',
          variant: 'destructive',
        });
        setIsDiscovering(false);
        setDiscoveryMethod(null);
        setMessages([]);
      } else {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to get AI response',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
      // Track AI conversation turn (each assistant response counts as a turn)
      if (!isInitial) {
        trackAIConversationTurn();
      }
      // Auto-save AI chat draft after each exchange
      if (discoveryMethod === 'chat') {
        setMessages(prev => {
          const capped = prev.slice(-50); // Cap at 50 messages
          saveDraft('chat', { schema_version: 1, messages: capped });
          return prev;
        });
      }
    }
  };

  const extractAndSaveValues = async () => {
    if (messages.length < 4) {
      toast({
        title: 'Keep going!',
        description: 'Have a bit more conversation so I can better understand your values.',
      });
      return;
    }

    setIsExtracting(true);

    try {
      // Get current session for authenticated request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discover-values`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages,
            action: 'extract',
            profileContext: {
              job_role: profile?.job_role || null,
              target_job_role: profile?.target_job_role || null,
              industry: profile?.industry || null,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429 && errorData.retry_after_seconds) {
          const seconds = errorData.retry_after_seconds;
          const timeMsg = seconds >= 60 ? `${Math.ceil(seconds / 60)} minute(s)` : `${seconds} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error(errorData.error || 'Failed to extract values');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              fullContent += content;
            }
          } catch {
            // Skip invalid JSON
          }
        }
      }

      // Parse the extracted values from the AI response
      // Try multiple patterns: raw JSON, markdown code blocks, or JSON with surrounding text
      let jsonString: string | null = null;

      // Pattern 1: JSON in markdown code block (```json ... ``` or ``` ... ```)
      const codeBlockMatch = fullContent.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (codeBlockMatch) {
        jsonString = codeBlockMatch[1].trim();
      }

      // Pattern 2: Raw JSON object (fallback)
      if (!jsonString) {
        const rawJsonMatch = fullContent.match(/\{[\s\S]*\}/);
        if (rawJsonMatch) {
          jsonString = rawJsonMatch[0];
        }
      }

      if (!jsonString) {
        console.error('Could not find JSON in AI response:', fullContent);
        throw new Error('Could not parse values from response. Please try again.');
      }

      let extracted;
      try {
        extracted = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Raw string:', jsonString);
        throw new Error('Invalid response format. Please try again.');
      }
      const values: ExtractedValue[] = extracted.values || [];

      // Save career context from extraction (fire-and-forget)
      const careerCtx: ExtractedCareerContext | undefined = extracted.career_context;
      if (careerCtx && user) {
        const saved = await saveCareerContextFromExtraction(user.id, profile, careerCtx);
        if (saved) {
          await invalidateProfile();
          emitEngagementEvent('career_context_completed', {
            fields_extracted: Object.entries(careerCtx).filter(([, v]) => v).map(([k]) => k),
          });
        }
      }

      if (values.length === 0) {
        toast({
          title: 'No values found',
          description: 'Try having a deeper conversation about what matters to you.',
        });
        return;
      }

      // Delete existing values before saving new ones (prevents append bug)
      await deleteExistingValues();

      // Save each value to the database
      let savedCount = 0;
      for (const value of values) {
        const { error } = await supabase.from('core_values').insert({
          user_id: user!.id,
          value_name: value.name,
          description: value.description,
          importance_rank: value.importance,
        });
        if (!error) savedCount++;
      }

      if (savedCount > 0) {
        // Track completion for AI conversation telemetry
        markCompleted();
        completeTelemetry(
          values.map(v => v.name),
          { ai_conversation_turns: Math.floor(messages.length / 2) }
        );

        toast({
          title: `${savedCount} values saved!`,
          description: 'Your core values have been added to your profile.',
        });
        fetchCoreValues();
        await notifyValuesChanged();
        // Delete draft after successful save
        await deleteDraft();
        setJustCompleted(true);
      }
    } catch (error) {
      console.error('Extract error:', error);
      toast({
        title: 'Error extracting values',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const deleteValue = async (id: string) => {
    const { error } = await supabase.from('core_values').delete().eq('id', id);
    if (!error) {
      setCoreValues(prev => prev.filter(v => v.id !== id));
      toast({ title: 'Value removed' });
    }
  };

  // Callback for child components to save draft data
  const handleSaveDraft = useCallback((method: DiscoveryMethod) => {
    return (data: Record<string, unknown>) => {
      saveDraft(method, data);
    };
  }, [saveDraft]);

  // Resume from a saved draft
  const handleResumeDraft = useCallback(() => {
    if (!draft) return;
    setDraftData(draft.draft_data);
    setDraftAssessmentId(draft.assessment_id);
    setDiscoveryMethod(draft.method);

    if (draft.method === 'chat') {
      const chatData = draft.draft_data as ChatDraftData;
      setMessages(chatData.messages || []);
      setIsDiscovering(true);
      // Restore telemetry session
      if (draft.assessment_id) {
        restoreSession(draft.assessment_id, 'ai_conversation');
      } else {
        startAssessment('ai_conversation', 'draft_resume');
      }
    }
  }, [draft, restoreSession, startAssessment]);

  // Start fresh — delete draft, show method selector
  const handleStartFresh = useCallback(async () => {
    await deleteDraft();
    setDraftData(null);
    setDraftAssessmentId(null);
    setDiscoveryMethod(null);
    setIsDiscovering(false);
    setMessages([]);
  }, [deleteDraft]);

  const handleMethodComplete = async (values: ExtractedValue[]) => {
    if (!user || values.length === 0) return;

    // Delete existing values before saving new ones (prevents append bug)
    await deleteExistingValues();

    let savedCount = 0;
    for (const value of values) {
      const { error } = await supabase.from('core_values').insert({
        user_id: user.id,
        value_name: value.name,
        description: value.description,
        importance_rank: value.importance,
      });
      if (!error) savedCount++;
    }

    if (savedCount > 0) {
      toast({
        title: `${savedCount} values saved!`,
        description: 'Your core values have been added to your profile.',
      });
      fetchCoreValues();
      await notifyValuesChanged();
      // Delete draft only after values are successfully saved
      await deleteDraft();
      setJustCompleted(true);
    }

    // Reset discovery state
    setDiscoveryMethod(null);
    setIsDiscovering(false);
    setMessages([]);
    setDraftData(null);
    setDraftAssessmentId(null);
  };

  const handleMethodCancel = () => {
    // Track abandonment if AI chat was in progress
    if (discoveryMethod === 'chat' && isDiscovering) {
      abandonAssessment('user_cancelled');
    }
    // Do NOT delete draft — progress should persist when leaving
    setDiscoveryMethod(null);
    setIsDiscovering(false);
    setMessages([]);
    setDraftData(null);
    setDraftAssessmentId(null);
  };

  const handleSelectMethod = (method: DiscoveryMethod) => {
    // If user already has values, show confirmation before proceeding
    if (valuesComplete) {
      setPendingMethod(method);
      return;
    }
    proceedWithMethod(method);
  };

  const proceedWithMethod = (method: DiscoveryMethod) => {
    setPendingMethod(null);
    setDiscoveryMethod(method);
    setDraftData(null);
    setDraftAssessmentId(null);
    if (method === 'chat') {
      setIsDiscovering(true);
      setMessages([]);
      // Start telemetry tracking for AI conversation
      startAssessment('ai_conversation', 'dashboard');
      sendMessage('', true);
    }
  };

  const hasEnoughConversation = messages.length >= 4;
  // AI enters synthesis mode at 12+ messages (~6 exchanges) — button becomes prominent
  const conversationMature = messages.length >= 12;

  const renderDiscoveryContent = () => {
    // Loading draft state — show skeleton briefly
    if (isDraftLoading) {
      return (
        <div className="flex-1 space-y-4 py-4">
          <Skeleton className="h-6 w-48 mx-auto" />
          <Skeleton className="h-4 w-64 mx-auto" />
          <div className="space-y-3 mt-6">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        </div>
      );
    }

    // No method selected — show resume prompt or method selector
    if (!discoveryMethod) {
      // Inline confirmation when user already has values and selected a new method
      if (pendingMethod) {
        return (
          <div className="flex-1 flex items-center justify-center">
            <div className="max-w-sm text-center space-y-4 p-6">
              <h3 className="text-lg font-semibold">Replace existing values?</h3>
              <p className="text-sm text-muted-foreground">
                Starting a new assessment will replace your current core values. This cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setPendingMethod(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => pendingMethod && proceedWithMethod(pendingMethod)}
                >
                  Replace Values
                </Button>
              </div>
            </div>
          </div>
        );
      }
      if (draft) {
        return (
          <div className="flex-1 overflow-auto space-y-4">
            <ResumeDraftPrompt
              draft={draft}
              onResume={handleResumeDraft}
              onStartFresh={handleStartFresh}
            />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or choose a different method</span>
              </div>
            </div>
            <DiscoveryMethodSelector onSelect={handleSelectMethod} />
          </div>
        );
      }
      return (
        <div className="flex-1 overflow-auto">
          <DiscoveryMethodSelector onSelect={handleSelectMethod} />
        </div>
      );
    }

    // Card Sort
    if (discoveryMethod === 'card-sort') {
      return (
        <div className="flex-1 overflow-auto">
          <ValuesCardSort
            onComplete={handleMethodComplete}
            onCancel={handleMethodCancel}
            initialDraftData={draftData ?? undefined}
            onSaveDraft={handleSaveDraft('card-sort')}
            assessmentId={draftAssessmentId}
          />
        </div>
      );
    }

    // Reflection Prompts
    if (discoveryMethod === 'reflection') {
      return (
        <div className="flex-1 overflow-auto">
          <ReflectionPrompts
            onComplete={handleMethodComplete}
            onCancel={handleMethodCancel}
            initialDraftData={draftData ?? undefined}
            onSaveDraft={handleSaveDraft('reflection')}
            assessmentId={draftAssessmentId}
          />
        </div>
      );
    }

    // Quick Assessment
    if (discoveryMethod === 'assessment') {
      return (
        <div className="flex-1 overflow-auto">
          <QuickAssessment
            onComplete={handleMethodComplete}
            onCancel={handleMethodCancel}
            initialDraftData={draftData ?? undefined}
            onSaveDraft={handleSaveDraft('assessment')}
            assessmentId={draftAssessmentId}
          />
        </div>
      );
    }

    // AI Chat (method === 'chat')
    return (
      <>
        <div className="max-h-[50vh] overflow-y-auto pr-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{
                    msg.role === 'assistant'
                      ? msg.content.replace(/\*{1,3}(.*?)\*{1,3}/g, '$1')
                      : msg.content
                  }</p>
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-4 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Extract Values Button - Shows after enough conversation, subdued until AI synthesis mode */}
        {hasEnoughConversation && (
          <div className="py-4 my-3 text-center">
            <Button
              onClick={extractAndSaveValues}
              disabled={isExtracting || isLoading}
              size="lg"
              variant={conversationMature ? "default" : "outline"}
              className={`w-full gap-2 transition-all duration-300 ${
                conversationMature
                  ? 'shadow-md'
                  : 'opacity-60 hover:opacity-80'
              }`}
            >
              {isExtracting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Extracting your values...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Save My Values
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              {conversationMature
                ? "We'll extract your core values from this conversation"
                : 'Keep chatting — the deeper we go, the better your values will be'}
            </p>
          </div>
        )}
        
        {/* Method change link above input */}
        <div className="flex justify-end mt-2">
          <Button variant="ghost" size="sm" onClick={handleMethodCancel} className="text-xs text-muted-foreground">
            Change method
          </Button>
        </div>
        {/* Input row */}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            className="flex-1 min-w-0"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Share your thoughts..."
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
            disabled={isLoading}
          />
          <Button
            size="icon"
            className="shrink-0 min-h-[44px] min-w-[44px]"
            onClick={() => sendMessage(input)}
            disabled={isLoading || !input.trim()}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </>
    );
  };

  const hasValues = coreValues.length > 0;
  const valuesComplete = coreValues.length >= 3;

  // Show celebration screen after values are saved for the first time
  if (justCompleted && coreValues.length >= 3) {
    return (
      <ValuesCelebration
        values={coreValues}
        onContinueToGoals={() => onNavigateToGoals?.()}
        onReviewValues={() => setJustCompleted(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Header */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          Core Values Discovery
        </h2>
        <p className="text-sm text-muted-foreground">
          Discover what matters most to guide your career decisions
        </p>
        {valuesComplete ? (
          <TabStatsLine 
            stats={[
              { value: '✓ Values complete', icon: <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> },
              { value: 'Unlocked all features', icon: <Unlock className="w-3.5 h-3.5" /> },
            ]}
          />
        ) : (
          <TabStatsLine 
            stats={[
              { value: `${coreValues.length}/3`, label: 'values discovered' },
              { value: `${3 - coreValues.length} more to unlock all features` },
            ]}
          />
        )}
      </div>

      <div className={`grid gap-6 ${hasValues ? 'lg:grid-cols-2' : ''}`}>
        {/* Discovered Values - Only show if user has values */}
        {hasValues && (
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                My Core Values
              </CardTitle>
              <CardDescription>
                Values that guide your decisions and define what matters most
              </CardDescription>
            </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {coreValues.map((value, index) => (
                <div
                  key={value.id}
                  className="p-4 rounded-lg bg-muted/50 border border-border/50 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          #{index + 1}
                        </Badge>
                        <span className="font-semibold">{value.value_name}</span>
                      </div>
                      {value.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {value.description}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => deleteValue(value.id)}
                      aria-label={`Delete value ${value.value_name}`}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discovery Methods */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent" />
                Value Discovery
              </CardTitle>
              <CardDescription>
                {discoveryMethod 
                  ? 'Follow the guided process to uncover your values'
                  : 'Choose your preferred discovery method'
                }
              </CardDescription>
            </div>
            {discoveryMethod && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMethodCancel}
                className="text-muted-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col min-h-[400px]">
          {renderDiscoveryContent()}
        </CardContent>
      </Card>
      </div>

    </div>
  );
};